/* taken from react-lazyload */

import React, {Component} from 'react';
import ReactDom from 'react-dom';
import {on, off} from './utils/event';

const defaultBoundingClientRect = {top: 0, right: 0, bottom: 0, left: 0, width: 0, height: 0};
const listeners = [];
let pending = [];

let passiveEventSupported;
try {
    const opts = Object.defineProperty({}, 'passive', {
        get() {
            passiveEventSupported = true;
        }
    });
    window.addEventListener('test', null, opts);
} catch (e) {
    passiveEventSupported = false;
}

// if they are supported, setup the optional params
// IMPORTANT: FALSE doubles as the default CAPTURE value!
const passiveEvent = passiveEventSupported ? {capture: false, passive: true} : false;


function checkNormalVisible(component) {
    const node = ReactDom.findDOMNode(component);

    if (!(node.offsetWidth || node.offsetHeight || node.getClientRects().length)) {
        return false;
    }

    let top;
    let elementHeight;

    try {
        ({top, height: elementHeight} = node.getBoundingClientRect());
    } catch (e) {
        ({top, height: elementHeight} = defaultBoundingClientRect);
    }

    const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;
    const offset = component.props.offset;

    return (top - offset <= windowInnerHeight) && (top + elementHeight + offset >= 0);
}


function checkVisible(component) {
    const node = ReactDom.findDOMNode(component);
    if (!node) {
        return;
    }

    const visible = checkNormalVisible(component);
    if (visible) {
        // Avoid extra render if previously is visible
        if (!component.visible) {
            pending.push(component);
            component.visible = true;
            component.forceUpdate();
        }
    } else if (!component.visible) {
        component.visible = false;
    }
}


function purgePending() {
    pending.forEach((component) => {
        const index = listeners.indexOf(component);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    });

    pending = [];
}


const lazyLoadHandler = () => {
    for (let i = 0; i < listeners.length; ++i) {
        const listener = listeners[i];
        checkVisible(listener);
    }
    purgePending();
};


class LazyLoad extends Component {
    constructor(props) {
        super(props);

        this.visible = false;
    }


    componentDidMount() {
        if (listeners.length === 0) {
            on(window, 'scroll', lazyLoadHandler, passiveEvent);
            on(window, 'resize', lazyLoadHandler, passiveEvent);
        }

        listeners.push(this);
        checkVisible(this);
    }

    shouldComponentUpdate() {
        return this.visible;
    }

    componentWillUnmount() {
        const index = listeners.indexOf(this);
        if (index !== -1) {
            listeners.splice(index, 1);
        }

        if (listeners.length === 0) {
            off(window, 'resize', lazyLoadHandler, passiveEvent);
            off(window, 'scroll', lazyLoadHandler, passiveEvent);
        }
    }

    render() {
        const defaultPlaceholder = <div style={{height: this.props.height}} className="lazyload-placeholder" />;

        if (this.visible) {
            return this.props.children;
        } else if (this.props.placeholder) {
            return this.props.placeholder || defaultPlaceholder;
        }

        return null;
    }
}

export default LazyLoad;
